/**
 * Shared masking-inventory load + merge logic, used by the in-flow masking InventoryPage and the
 * standalone Inventory Manager (Profile). Single source of truth for the "saved is the truth,
 * recommendation is advice" model so the two hosts can never drift.
 */
import {
  getLatestDataSourceInventory,
  inventoryContentOf,
  inventoryToSelectedFields,
} from 'Secure/api';
import {
  discoverTablesToFileTableMeta,
  discoverTablesToMaskingSelectedFields,
  loadDiscoverOverviewTables,
} from 'Secure/secureDiscoverFields';
import { hasFindingEntries, resolveRecommendedMaskingAlgorithm, topFindingEntity } from 'Secure/Utils';
import { recommendedAlgorithmOverride, recommendedParamsFor } from 'Secure/valueFormatMapping';

/**
 * Resolve a field's recommendation from findings + the provider entity→algorithm map, then apply
 * any value_formats-driven override. Returns both the recommended algorithm (base or overridden
 * alias, e.g. a LAST_FIRST person column → PERSON_LAST_FIRST) and the recommended flat params
 * (dateFormat / nameOrder). `valueFormats` is the discovery block carried on the descriptor;
 * both the params and the override key off the same top finding used for the base algorithm.
 */
function resolveRecommendation(findings, valueFormats, map) {
  const base = resolveRecommendedMaskingAlgorithm(findings, map) || '';
  const findingType = topFindingEntity(findings);
  const recommendedAlgorithm = recommendedAlgorithmOverride(valueFormats, findingType, base) || base;
  const recommendedParams = recommendedParamsFor(valueFormats, findingType);
  return { recommendedAlgorithm, recommendedParams };
}

/** Plain-object guard: return `obj` when it's a non-array object, else `{}`. */
function paramsObject(obj) {
  return obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : {};
}

/**
 * Re-key bare saved-table keys onto their schema-qualified discovery twin when unambiguous.
 * Inventories saved while discovery keys were unqualified (schemaName '') read back with bare
 * table-name keys; discovery keys are now `schema.table` for multi-schema sources, so those
 * saved entries would otherwise orphan next to their own table's discovery row. If exactly one
 * discovery table has that bare name, they are the same table — re-key so the saved
 * algorithm/checked land on it (and the next Save persists the qualified schema, self-healing
 * the inventory). An ambiguous bare name (the saved entry was itself a collision artifact —
 * its true schema is unknowable) is kept as-is: it renders as a schema-less saved-only group
 * the user can clear or re-save, rather than being silently dropped or mis-assigned.
 */
export function normalizeSavedTableKeys(inventoryFields, discoverFields) {
  const byBareName = {};
  Object.keys(discoverFields || {}).forEach((k) => {
    const dot = k.indexOf('.');
    const bare = dot > 0 ? k.slice(dot + 1) : k;
    (byBareName[bare] = byBareName[bare] || []).push(k);
  });
  const out = {};
  for (const key of Object.keys(inventoryFields || {})) {
    const isBareOrphan = !key.includes('.') && !(discoverFields || {})[key];
    const candidates = isBareOrphan ? byBareName[key] || [] : [];
    out[candidates.length === 1 ? candidates[0] : key] = inventoryFields[key];
  }
  return out;
}

// Saved inventory is the source of truth; the discovery recommendation is advice shown beside it.
// A saved field keeps its `algorithm` verbatim (never recomputed); a field with no saved entry
// (`saved: false`) is seeded from the recommendation. `recommendedAlgorithm` is display-only.
export function mergeMaskingInventoryWithDiscover(inventoryFields, discoverFields, entityAlgorithmMap = {}) {
  const map = entityAlgorithmMap && typeof entityAlgorithmMap === 'object' ? entityAlgorithmMap : {};
  inventoryFields = normalizeSavedTableKeys(inventoryFields, discoverFields);
  const tables = new Set([
    ...Object.keys(inventoryFields || {}),
    ...Object.keys(discoverFields || {}),
  ]);
  const result = {};
  for (const table of tables) {
    const invT = (inventoryFields && inventoryFields[table]) || {};
    const disT = (discoverFields && discoverFields[table]) || {};
    const fieldNames = new Set([...Object.keys(invT), ...Object.keys(disT)]);
    result[table] = {};
    for (const field of fieldNames) {
      const inv = invT[field];
      const dis = disT[field];
      const findings =
        dis && hasFindingEntries(dis.findings)
          ? dis.findings
          : inv && inv.findings != null
            ? inv.findings
            : {};
      const saved = inv != null;
      // value_formats live on the discovery descriptor; a saved-only field (dropped from a later
      // discovery re-run) may still carry them from a prior merge persisted into the inventory.
      const valueFormats = (dis && dis.valueFormats) || (inv && inv.valueFormats) || null;
      // Same rule for Profile-sourced key constraints: discovery is the source of truth, a
      // saved-only field keeps what a prior merge persisted.
      const keyConstraints = (dis && dis.keyConstraints) || (inv && inv.keyConstraints) || null;
      // A real engine type from discovery beats a saved 'string' placeholder (the pre-V32
      // default every saved column carried); a saved non-placeholder type still wins.
      const dataType =
        inv && inv.dataType && inv.dataType !== 'string'
          ? inv.dataType
          : (dis && dis.dataType) || (inv && inv.dataType) || 'string';
      const { recommendedAlgorithm, recommendedParams } = resolveRecommendation(findings, valueFormats, map);
      const checked = saved
        ? Boolean(inv.checked)
        : dis != null
          ? Boolean(dis.checked)
          : false;
      const algorithm = saved
        ? (inv.algorithm != null ? String(inv.algorithm).trim() : '') // saved truth, verbatim
        : recommendedAlgorithm; // seed only when not saved
      // Params mirror the algorithm/recommendedAlgorithm split: a saved field keeps its saved
      // params verbatim; an unsaved field is seeded from the recommendation. recommendedParams
      // stays display/apply-only either way.
      const params = saved ? paramsObject(inv.params) : recommendedParams;
      result[table][field] = {
        dataType,
        findings,
        checked,
        algorithm,
        recommendedAlgorithm,
        params,
        recommendedParams,
        valueFormats,
        keyConstraints,
        saved,
      };
    }
  }
  return result;
}

// Recommendation column only: re-derive each field's `recommendedAlgorithm` from the (new) provider
// map, leaving `algorithm`, `checked`, and `saved` untouched. Never mutates the saved value.
export function attachRecommendations(selectedFields, entityAlgorithmMap = {}) {
  const map = entityAlgorithmMap && typeof entityAlgorithmMap === 'object' ? entityAlgorithmMap : {};
  const out = {};
  for (const t of Object.keys(selectedFields || {})) {
    out[t] = {};
    for (const f of Object.keys(selectedFields[t])) {
      const fd = selectedFields[t][f];
      const { recommendedAlgorithm, recommendedParams } = resolveRecommendation(fd.findings, fd.valueFormats, map);
      out[t][f] = {
        ...fd,
        recommendedAlgorithm,
        recommendedParams,
      };
    }
  }
  return out;
}

/**
 * Re-point the inventory at a different masking provider's algorithm vocabulary. Re-derives every
 * field's `recommendedAlgorithm` from the new provider's map AND resets each field's `algorithm`
 * to that recommendation — discarding prior selections. A provider switch invalidates the existing
 * inventory wholesale: the old algorithm ids (e.g. a C2 FPE_EBCDIC override) are meaningless to the
 * new provider, so nothing is preserved. A field whose recommendation resolves to none (the latest
 * discover found nothing) is fully cleared — algorithm AND checked — so a stale saved selection
 * can't survive as `sensitive: true` with no algorithm. Not persisted until the user clicks
 * Mask/Save.
 */
export function resetAllToRecommended(selectedFields, entityAlgorithmMap = {}) {
  const map = entityAlgorithmMap && typeof entityAlgorithmMap === 'object' ? entityAlgorithmMap : {};
  const out = {};
  for (const t of Object.keys(selectedFields || {})) {
    out[t] = {};
    for (const f of Object.keys(selectedFields[t])) {
      const fd = selectedFields[t][f];
      const { recommendedAlgorithm, recommendedParams } = resolveRecommendation(fd.findings, fd.valueFormats, map);
      out[t][f] = {
        ...fd,
        recommendedAlgorithm,
        recommendedParams,
        algorithm: recommendedAlgorithm,
        params: recommendedParams,
        ...(recommendedAlgorithm ? {} : { checked: false }),
      };
    }
  }
  return out;
}

/**
 * Uncheck every field and clear its algorithm — the staged "mask nothing" state. The counterpart
 * of resetAllToRecommended: recommendations stay visible in the RECOMMENDED column, but no field
 * is selected, so saving/masking from this state masks nothing. Staged only; persisted on Save.
 */
export function clearAllSelections(selectedFields) {
  const out = {};
  for (const t of Object.keys(selectedFields || {})) {
    out[t] = {};
    for (const f of Object.keys(selectedFields[t])) {
      out[t][f] = {
        ...selectedFields[t][f],
        checked: false,
        algorithm: '',
        // No algorithm ⇒ no params to carry (they'd be orphaned flat keys on save).
        params: {},
      };
    }
  }
  return out;
}

export function ensureMaskingFieldShape(obj) {
  const out = {};
  for (const t of Object.keys(obj || {})) {
    out[t] = {};
    for (const f of Object.keys(obj[t])) {
      const r = obj[t][f];
      out[t][f] = {
        dataType: r.dataType || 'string',
        findings: r.findings != null ? r.findings : {},
        checked: Boolean(r.checked),
        algorithm: r.algorithm != null ? String(r.algorithm) : '',
        recommendedAlgorithm: r.recommendedAlgorithm != null ? String(r.recommendedAlgorithm) : '',
        params: paramsObject(r.params),
        recommendedParams: paramsObject(r.recommendedParams),
        valueFormats: r.valueFormats != null ? r.valueFormats : null,
        saved: Boolean(r.saved),
      };
    }
  }
  return out;
}

/**
 * Full load path for one data source: merge a saved inventory's content with the latest discovery
 * overview. Under the multi-inventory model the caller may pass the inventory row it wants merged
 * (`inventory`); when omitted, the source's most recently updated inventory is fetched. Pass
 * `inventory: null` explicitly to merge discover-only (a fresh draft).
 * Returns `{ selectedFields, hasSavedInventory, noDiscoverFields, fileTableMeta }`.
 *   - hasSavedInventory — a persisted inventory row was merged (drives create-vs-update flows and
 *     the manager's "scanned but unconfigured" banner).
 *   - noDiscoverFields — discovery returned nothing (no completed discover run for this source).
 *   - fileTableMeta — per-table {sniffedType, delimiter} from the discovery overview (file
 *     sources; empty object otherwise) for format-based maskability and save-time routing.
 * @param {{ onWarning?: (notification: { header: string, body: string }) => void }} [opts]
 *   Passed through to loadDiscoverOverviewTables — surfaces the table-index inventory-cap
 *   warning for very large (PeopleSoft-80K-scale) runs.
 */
export async function loadMergedInventory(dataSourceId, entityAlgorithmMap = {}, preferredRunId, inventory, opts = {}) {
  if (inventory === undefined) {
    try {
      inventory = await getLatestDataSourceInventory(dataSourceId);
    } catch (e) {
      inventory = null;
    }
  }
  const hasSavedInventory = inventory != null;
  const selectedFromInventory = inventoryToSelectedFields(inventoryContentOf(inventory));

  const overviewTables = await loadDiscoverOverviewTables(dataSourceId, preferredRunId, opts);
  const fileTableMeta = discoverTablesToFileTableMeta(overviewTables);
  const discoverMap = discoverTablesToMaskingSelectedFields(overviewTables);
  const merged = mergeMaskingInventoryWithDiscover(selectedFromInventory, discoverMap, entityAlgorithmMap);

  if (Object.keys(merged).length > 0) {
    return { selectedFields: merged, hasSavedInventory, noDiscoverFields: false, fileTableMeta };
  }
  return {
    selectedFields: attachRecommendations(ensureMaskingFieldShape(discoverMap), entityAlgorithmMap),
    hasSavedInventory,
    noDiscoverFields: true,
    fileTableMeta,
  };
}
